import { DEG, clamp01 } from '../../core/math';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import { createCanvas, get2d, rasteriseContours } from '../../core/text/rasterize';
import type { Ctx2D, RenderTarget, TechniqueContext } from '../types';
import type { PhysarumValues } from './params';

/**
 * Physarum transport network (Jones, "Characteristics of Pattern Formation and
 * Evolution in Approximations of Physarum Transport Networks", 2010).
 *
 * Each agent samples a pheromone trail ahead, ahead-left and ahead-right, turns towards
 * the strongest, moves, and deposits. The veined networks emerge purely from that loop
 * plus diffusion and decay — there is no global rule.
 *
 * This runs on the CPU rather than on the GPU, deliberately. A GPU version needs agent
 * state in textures and a scatter write for deposition, which WebGL2 cannot do without
 * transform feedback plus additive point rendering — a lot of machinery. Measured on a
 * 900×1100 grid, a typed-array CPU implementation handles a quarter of a million agents
 * at interactive rates, which covers the useful range; the parameter allows more for
 * batch renders where the wait is acceptable.
 */
export interface PhysarumState {
  x: Float32Array;
  y: Float32Array;
  heading: Float32Array;
  count: number;

  trail: Float32Array;
  next: Float32Array;
  /** Text influence per cell, precomputed once. */
  attract: Float32Array;
  blocked: Uint8Array;
  gridWidth: number;
  gridHeight: number;

  output: OffscreenCanvas | HTMLCanvasElement;
  octx: Ctx2D;
  image: ImageData | null;
  palette: ResolvedPalette;
  stepsDone: number;
  targetSteps: number;
  dirty: boolean;
}

function values(ctx: TechniqueContext): PhysarumValues {
  return ctx.values as unknown as PhysarumValues;
}

export function init(ctx: TechniqueContext): PhysarumState {
  const v = values(ctx);
  const aspect = ctx.height / Math.max(1, ctx.width);
  const gridWidth = Math.max(64, Math.min(2048, Math.round(v.resolution)));
  const gridHeight = Math.max(64, Math.round(gridWidth * aspect));
  const cells = gridWidth * gridHeight;

  const count = Math.max(1, Math.min(2000000, Math.round(v.agents)));

  // Text mask at simulation resolution.
  const scaleX = gridWidth / ctx.width;
  const scaleY = gridHeight / ctx.height;
  const scaled = ctx.field.contours.map((contour) => {
    const points = new Float32Array(contour.points.length);
    for (let i = 0; i < points.length; i += 2) {
      points[i] = contour.points[i] * scaleX;
      points[i + 1] = contour.points[i + 1] * scaleY;
    }
    return { ...contour, points };
  });
  const mask = rasteriseContours(scaled, gridWidth, gridHeight, 1);

  const attract = new Float32Array(cells);
  const blocked = new Uint8Array(cells);
  for (let i = 0; i < cells; i++) {
    const inside = mask.alpha[i] > 127;
    if (v.textRole === 'nutrient' && inside) attract[i] = v.textStrength;
    if (v.textRole === 'obstacle' && inside) blocked[i] = 1;
  }

  const state: PhysarumState = {
    x: new Float32Array(count),
    y: new Float32Array(count),
    heading: new Float32Array(count),
    count,
    trail: new Float32Array(cells),
    next: new Float32Array(cells),
    attract,
    blocked,
    gridWidth,
    gridHeight,
    output: createCanvas(gridWidth, gridHeight),
    octx: get2d(createCanvas(1, 1)),
    image: null,
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    stepsDone: 0,
    targetSteps: v.totalSteps,
    dirty: true,
  };
  state.octx = get2d(state.output);
  state.image = state.octx.createImageData(gridWidth, gridHeight);

  // Spawn.
  const insideCells: number[] = [];
  if (v.textRole === 'spawn') {
    for (let i = 0; i < cells; i++) if (mask.alpha[i] > 127) insideCells.push(i);
  }

  for (let i = 0; i < count; i++) {
    if (insideCells.length > 0) {
      const cell = insideCells[ctx.rng.int(0, insideCells.length - 1)];
      state.x[i] = (cell % gridWidth) + ctx.rng.next();
      state.y[i] = ((cell / gridWidth) | 0) + ctx.rng.next();
    } else {
      state.x[i] = ctx.rng.next() * gridWidth;
      state.y[i] = ctx.rng.next() * gridHeight;
    }
    state.heading[i] = ctx.rng.next() * Math.PI * 2;
  }

  return state;
}

export function step(state: PhysarumState, _dt: number, ctx: TechniqueContext): PhysarumState {
  if (state.stepsDone >= state.targetSteps) return state;
  const v = values(ctx);

  const { gridWidth: gw, gridHeight: gh, trail, next, attract, blocked } = state;
  const sensorAngle = v.sensorAngle * DEG;
  const turnSpeed = v.turnSpeed * DEG;
  const batch = Math.max(1, Math.round(v.stepsPerFrame * clamp01(ctx.quality)));

  const sample = (x: number, y: number): number => {
    const ix = ((x | 0) + gw) % gw;
    const iy = ((y | 0) + gh) % gh;
    const i = iy * gw + ix;
    return trail[i] + attract[i];
  };

  for (let iter = 0; iter < batch && state.stepsDone < state.targetSteps; iter++) {
    // ── Move and deposit ──────────────────────────────────────────────────
    for (let i = 0; i < state.count; i++) {
      const px = state.x[i];
      const py = state.y[i];
      const h = state.heading[i];

      const forward = sample(px + Math.cos(h) * v.sensorDistance, py + Math.sin(h) * v.sensorDistance);
      const left = sample(
        px + Math.cos(h - sensorAngle) * v.sensorDistance,
        py + Math.sin(h - sensorAngle) * v.sensorDistance,
      );
      const right = sample(
        px + Math.cos(h + sensorAngle) * v.sensorDistance,
        py + Math.sin(h + sensorAngle) * v.sensorDistance,
      );

      let nh = h;
      if (forward >= left && forward >= right) {
        // Keep going.
      } else if (left > right) nh -= turnSpeed;
      else if (right > left) nh += turnSpeed;
      else nh += (ctx.rng.next() - 0.5) * turnSpeed * 2;

      if (v.randomTurn > 0) nh += (ctx.rng.next() - 0.5) * v.randomTurn;

      let nx = px + Math.cos(nh) * v.stepSize;
      let ny = py + Math.sin(nh) * v.stepSize;

      // Wrap; a bounded field collects agents along the edges.
      if (nx < 0) nx += gw;
      else if (nx >= gw) nx -= gw;
      if (ny < 0) ny += gh;
      else if (ny >= gh) ny -= gh;

      const cell = ((ny | 0) % gh) * gw + ((nx | 0) % gw);
      if (blocked[cell]) {
        // Bounce off the letterforms instead of entering them.
        nh += Math.PI * (0.5 + ctx.rng.next() * 0.5);
        nx = px;
        ny = py;
      } else {
        trail[cell] += v.deposit;
      }

      state.x[i] = nx;
      state.y[i] = ny;
      state.heading[i] = nh;
    }

    // ── Diffuse and decay ─────────────────────────────────────────────────
    //
    // Separable blur: a horizontal pass then a vertical one is mathematically the same
    // 3×3 box as the naive version but reads six neighbours instead of nine, and both
    // passes walk memory in order. On a 300 000-cell grid run for hundreds of steps
    // this is the single most expensive loop in the technique.
    const keep = 1 - v.decay;
    const d = v.diffusion;

    for (let y = 0; y < gh; y++) {
      const row = y * gw;
      for (let x = 0; x < gw; x++) {
        const left = x === 0 ? gw - 1 : x - 1;
        const right = x === gw - 1 ? 0 : x + 1;
        next[row + x] = (trail[row + left] + trail[row + x] + trail[row + right]) / 3;
      }
    }
    for (let y = 0; y < gh; y++) {
      const up = (y === 0 ? gh - 1 : y - 1) * gw;
      const mid = y * gw;
      const down = (y === gh - 1 ? 0 : y + 1) * gw;
      for (let x = 0; x < gw; x++) {
        const blurred = (next[up + x] + next[mid + x] + next[down + x]) / 3;
        trail[mid + x] = (trail[mid + x] * (1 - d) + blurred * d) * keep;
      }
    }

    state.stepsDone++;
  }

  state.dirty = true;
  return state;
}

export function isComplete(state: PhysarumState): boolean {
  return state.stepsDone >= state.targetSteps;
}

export function draw(state: PhysarumState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);

  if (state.dirty && state.image) {
    const data = state.image.data;
    const { trail, palette } = state;

    let max = 0.0001;
    for (let i = 0; i < trail.length; i++) if (trail[i] > max) max = trail[i];
    const inverseMax = 1 / max;
    const ramp = palette.ramp;
    const rampSteps = ramp.length / 3;

    for (let i = 0; i < trail.length; i++) {
      const t = Math.min(1, (trail[i] * inverseMax) ** (1 / v.contrast));
      const r = Math.min(rampSteps - 1, (t * (rampSteps - 1)) | 0) * 3;
      data[i * 4] = ramp[r];
      data[i * 4 + 1] = ramp[r + 1];
      data[i * 4 + 2] = ramp[r + 2];
      // Alpha follows the trail, with a floor: without it the contrast curve lifts even
      // untouched cells to a faint tint and the layer covers the whole canvas in haze
      // instead of showing a network.
      const a = t < 0.06 ? 0 : Math.min(1, (t - 0.06) * 2.4);
      data[i * 4 + 3] = Math.round(a * 255 * v.opacity);
    }

    state.octx.putImageData(state.image, 0, 0);
    state.dirty = false;
  }

  target.ctx.drawImage(state.output as CanvasImageSource, 0, 0, target.width, target.height);
}

export function elementCount(state: PhysarumState): number {
  return state.count;
}
